import React from "react";
import { useNavigate } from "react-router-dom";
import loginToken from "../authComponents/localStorageItems/LoginTokin";
import { handleCompatibilityTest } from "../../utils/compatibilityTest";

/**
 * A compelling homepage section that highlights the Compatibility Assessment.
 * Placed between FeaturesSection and ProcessSection on the landing page.
 */
const CompatibilitySection = ({ setLoginPopUp }) => {
  const navigate = useNavigate();

  const handleCTA = () => {
    if (loginToken) {
      handleCompatibilityTest();
    } else if (setLoginPopUp) {
      setLoginPopUp(true);
    } else {
      navigate("/login");
    }
  };

  return (
    <section className="relative py-20 sm:py-28 overflow-hidden" style={{ background: "#0f0303" }}>
      {/* Background pattern & gradient */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: "32px 32px",
        }} />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, #8B1E1E 0%, transparent 70%)" }} />

      <div className="relative max-w-[1200px] mx-auto px-5">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* LEFT: Content */}
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
              style={{ background: "rgba(139,30,30,0.2)", color: "#f5a5a5" }}>
              Compatibility Assessment
            </span>

            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-[2.8rem] font-bold text-white mb-6 leading-tight">
              Don't Leave Your Marriage{" "}
              <span style={{ color: "#f5c6c6" }}>to Chance</span>
            </h2>

            <p className="text-white/60 text-lg leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
              Research shows that <strong className="text-white/80">Muslim couples who discuss values, finances, and family
              expectations before marriage</strong> are significantly happier in the long run. Our expert-designed
              Compatibility Assessment covers the 8 dimensions that matter most — so you can find
              someone who truly complements your life.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-8 mb-10">
              {[
                { value: "8", label: "Life Dimensions", desc: "Values, family, finance & more" },
                { value: "5 min", label: "Quick Assessment", desc: "Designed to respect your time" },
                { value: "100%", label: "Private & Secure", desc: "Only scores are shared" },
              ].map((stat, i) => (
                <div key={i} className="text-center lg:text-left">
                  <p className="text-2xl sm:text-3xl font-bold" style={{ color: "#f5a5a5" }}>{stat.value}</p>
                  <p className="text-white/70 text-sm font-semibold">{stat.label}</p>
                  <p className="text-white/30 text-xs mt-0.5">{stat.desc}</p>
                </div>
              ))}
            </div>

            <button onClick={handleCTA}
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-base transition-all duration-300 hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #8B1E1E, #c53030)",
                boxShadow: "0 8px 32px rgba(139,30,30,0.4)",
              }}>
              {loginToken ? "Take the Assessment" : "Sign Up & Discover Compatibility"}
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>

          {/* RIGHT: Visual card */}
          <div className="flex-shrink-0 w-full max-w-md lg:max-w-sm">
            <div className="relative">
              {/* Glow behind card */}
              <div className="absolute inset-4 rounded-3xl blur-3xl opacity-30"
                style={{ background: "#8B1E1E" }} />

              {/* Card */}
              <div className="relative rounded-3xl overflow-hidden"
                style={{
                  background: "linear-gradient(145deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
                  border: "1px solid rgba(255,255,255,0.08)",
                  backdropFilter: "blur(20px)",
                }}>
                <div className="p-8">
                  {/* Fake compatibility result preview */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
                      style={{ background: "rgba(139,30,30,0.2)" }}>
                      <span className="text-3xl">💑</span>
                    </div>
                    <p className="text-white/40 text-xs uppercase tracking-widest">Your Compatibility Score</p>
                  </div>

                  {/* Dimension bars */}
                  <div className="space-y-3.5">
                    {[
                      { name: "Religious Values", icon: "🕌", pct: 92 },
                      { name: "Emotional Bond", icon: "💭", pct: 87 },
                      { name: "Family Goals", icon: "👨‍👩‍👧‍👦", pct: 78 },
                      { name: "Financial Mindset", icon: "📊", pct: 85 },
                      { name: "Communication", icon: "💬", pct: 90 },
                      { name: "Lifestyle Match", icon: "🌿", pct: 73 },
                      { name: "Future Vision", icon: "🔮", pct: 81 },
                    ].map((dim, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white/50 text-xs flex items-center gap-1.5">
                            <span>{dim.icon}</span> {dim.name}
                          </span>
                          <span className="text-white/70 text-xs font-semibold">{dim.pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: `${dim.pct}%`,
                              background: dim.pct >= 85
                                ? "linear-gradient(90deg, #059669, #10b981)"
                                : dim.pct >= 70
                                  ? "linear-gradient(90deg, #D97706, #F59E0B)"
                                  : "linear-gradient(90deg, #DC2626, #EF4444)",
                            }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Overall score */}
                  <div className="mt-6 pt-5 border-t border-white/5 text-center">
                    <p className="text-white/30 text-xs mb-1">Overall Compatibility</p>
                    <p className="text-3xl font-bold" style={{ color: "#f5a5a5", fontFamily: "'Playfair Display', serif" }}>
                      83.7%
                    </p>
                    <p className="text-white/20 text-[10px] mt-1 italic">Preview — your real score awaits</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom trust badges */}
        <div className="mt-16 flex flex-wrap justify-center gap-8 sm:gap-12">
          {[
            { icon: "🧠", text: "Designed with psychologist input" },
            { icon: "🔐", text: "Answers always private" },
            { icon: "📊", text: "Scientifically weighted scoring" },
            { icon: "♾️", text: "Unlimited compatibility checks" },
          ].map((badge, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-lg">{badge.icon}</span>
              <span className="text-white/30 text-xs sm:text-sm">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CompatibilitySection;
